import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useT } from "../../../i18n/I18nProvider";
import {
  createProjectFromEstimate, downloadDocPdf, getSendPreview, markInvoicePaid, type EmailMeta, type SalesSendPreview,
} from "../../../lib/sales";
import { useToast } from "../../ui";
import { DOC_CONFIG, SHARE_TYPE, type AnyDoc, type DocKind } from "./config";

/**
 * Everything a single sales document can be asked to do, and the state that
 * says whether it is busy doing it.
 *
 * Kept out of `DocDetail` so the page is only layout. Seven mutations, each with
 * its own error path and its own idea of what "done" means (a status change
 * stays put, a convert navigates away, a delete goes back to the list), is a
 * second responsibility, and it is the one that gets edited when the API moves.
 *
 * Every outcome speaks through the toast rather than through a line of state
 * the page has to render: these are receipts for something the operator just
 * pressed, and by the time the next one arrives the previous is not news.
 */
export interface DocActions {
  doc: AnyDoc | null;
  loading: boolean;
  /** A mutation is in flight; the head's buttons stand down while it is. */
  busy: boolean;
  /** The PDF is being built, which is slower and has its own spinner. */
  pdfBusy: boolean;
  reload: () => void;
  changeStatus: (status: string) => void;
  /** Opens the confirmation with the letter the send would mail. */
  send: () => void;
  /** The letter waiting on the operator's yes, or null when none is open. */
  sendPreview: SalesSendPreview | null;
  cancelSend: () => void;
  /** Mail it: the operator has read the preview and said yes. */
  confirmSend: () => void;
  downloadPdf: () => void;
  convert: () => void;
  /** Start the work a won estimate was accepted for. Estimates only. */
  createProject: () => void;
  markPaid: () => void;
  remove: () => void;
}

export function useDocActions(kind: DocKind, id: string | undefined): DocActions {
  const t = useT();
  const toast = useToast();
  const nav = useNavigate();
  const cfg = DOC_CONFIG[kind];

  const [doc, setDoc] = useState<AnyDoc | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [sendPreview, setSendPreview] = useState<SalesSendPreview | null>(null);

  const reload = useCallback(() => {
    if (id) cfg.api.get(id).then(setDoc);
  }, [cfg, id]);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    cfg.api
      .get(id)
      .then(setDoc)
      .finally(() => setLoading(false));
  }, [cfg, id]);

  const changeStatus = useCallback(
    (status: string) => {
      if (!doc) return;
      setBusy(true);
      cfg.api
        .update(doc.id, { status })
        .then((next) => {
          setDoc(next);
          toast(t(`sales.status.${status}`));
        })
        .catch(() => toast(t("common.error")))
        .finally(() => setBusy(false));
    },
    [cfg, doc, t, toast],
  );

  /**
   * Pressing send shows the letter first. The one-click send mails a text the
   * operator did not write, so the From, the To and the words are read in the
   * confirmation before anything leaves - the same last stop every other
   * manual send in the CRM has. Nothing is sent until `confirmSend`.
   */
  const send = useCallback(() => {
    const type = SHARE_TYPE[kind];
    if (!doc || !cfg.api.send || !type) return;
    setBusy(true);
    getSendPreview(type, doc.id)
      .then(setSendPreview)
      .catch(() => toast(t("common.error")))
      .finally(() => setBusy(false));
  }, [cfg, doc, kind, t, toast]);

  const cancelSend = useCallback(() => setSendPreview(null), []);

  const confirmSend = useCallback(() => {
    if (!doc || !cfg.api.send) return;
    setBusy(true);
    cfg.api
      .send(doc.id)
      .then((res) => {
        setDoc(res);
        setSendPreview(null);
        const meta = (res as { email?: EmailMeta }).email;
        const key = meta ? sendResultKey(meta) : null;
        if (key) toast(t(`sales.sendResult.${key}`));
      })
      .catch(() => toast(t("common.error")))
      .finally(() => setBusy(false));
  }, [cfg, doc, t, toast]);

  const downloadPdf = useCallback(() => {
    if (!doc) return;
    setPdfBusy(true);
    downloadDocPdf(kind, doc.id, `${doc.number}.pdf`)
      .catch(() => toast(t("common.error")))
      .finally(() => setPdfBusy(false));
  }, [doc, kind, t, toast]);

  const convert = useCallback(() => {
    const conv = cfg.convert;
    if (!doc || !conv) return;
    setBusy(true);
    conv
      .run(doc.id)
      .then((res) => {
        const target = res.estimate ?? res.invoice;
        if (target) nav(`/sales/${conv.toKind}/${target.id}`);
      })
      .catch((e: unknown) => {
        // Already converted once. The server says which document it became, and
        // going there is more use than telling the operator it failed.
        const err = e as { body?: { estimateId?: string; invoiceId?: string } };
        const tid = err.body?.estimateId ?? err.body?.invoiceId;
        if (tid) nav(`/sales/${conv.toKind}/${tid}`);
        else toast(t("common.error"));
      })
      .finally(() => setBusy(false));
  }, [cfg, doc, nav, t, toast]);

  /**
   * Start the project a won estimate was accepted for.
   *
   * Not a convert: the estimate stays exactly where it is and gains a
   * `projectId`, so unlike `convert` above there is no second document to land
   * on - the destination is the project itself, in another section.
   *
   * The server refuses with 409 `already_has_project` when this quote has been
   * started already, by an earlier click or by a second operator racing this
   * one. That refusal is the feature: two projects with the same client, budget
   * and name, minutes apart, and nothing afterwards could say which one the
   * hours belong on. So the refusal is reported and then followed - the id in
   * the body is re-read from the winner on the server, which makes it the
   * project that actually exists. Naming a record and leaving the operator to
   * find it by hand is the one thing an error like this must not do.
   */
  const createProject = useCallback(() => {
    if (!doc) return;
    setBusy(true);
    createProjectFromEstimate(doc.id, {})
      .then((res) => {
        toast(t("projects.projectCreated"));
        nav(`/projects/${res.project.id}`);
      })
      .catch((e: unknown) => {
        const err = e as { body?: { error?: string; projectId?: string | null } };
        if (err.body?.error !== "already_has_project") {
          toast(t("common.error"));
          return;
        }
        toast(t("projects.estimateHasProject"));
        // `projectId` is null only if the link vanished between the refusal and
        // the server's re-read. There is nowhere to send the operator then, so
        // re-read the estimate instead: the head swaps the button for a link the
        // moment the document says it has a project.
        if (err.body.projectId) nav(`/projects/${err.body.projectId}`);
        else reload();
      })
      .finally(() => setBusy(false));
  }, [doc, nav, reload, t, toast]);

  const markPaid = useCallback(() => {
    if (!doc) return;
    setBusy(true);
    markInvoicePaid(doc.id)
      .then((next) => {
        setDoc(next);
        toast(t("sales.status.paid"));
      })
      .catch(() => toast(t("common.error")))
      .finally(() => setBusy(false));
  }, [doc, t, toast]);

  const remove = useCallback(() => {
    if (!doc) return;
    cfg.api
      .del(doc.id)
      .then(() => nav(`/sales/${kind}`))
      .catch(() => toast(t("common.error")));
  }, [cfg, doc, kind, nav, t, toast]);

  return {
    doc, loading, busy, pdfBusy, reload, changeStatus, send, sendPreview, cancelSend, confirmSend,
    downloadPdf, convert, createProject, markPaid, remove,
  };
}

/**
 * What the send endpoint actually did with the email, as an i18n segment.
 *
 * "Sent" on the button is a status change; whether a message left the building
 * is a separate fact, and the operator has to be told when it did not.
 */
function sendResultKey(meta: EmailMeta): string | null {
  if (meta.reason === "no_recipient") return "noEmail";
  if (meta.status === "sent") return "sent";
  if (meta.status === "dev") return "dev";
  if (meta.status === "failed") return "failed";
  return null;
}
